import { NotificationDto } from '@dto/notification-list/notification.dto';
import { getInitialNotificationPagination, NotificationPagination } from '@entity/notification-pagination.entity';
import { createReducer, on } from '@ngrx/store';
import { NullableType } from '@type/nullable.type';
import {
  loadNotificationListTeamsChats,
  loadNotificationListTeamsChatsFailure,
  loadNotificationListTeamsChatsSuccess,
  loadNotificationListTeamsChannels,
  loadNotificationListTeamsChannelsFailure,
  loadNotificationListTeamsChannelsSuccess,
  loadNotificationListTeamsCampaigns,
  loadNotificationListTeamsCampaignsFailure,
  loadNotificationListTeamsCampaignsSuccess,
  setActiveTab,
  initActiveTab,
} from './notification-list-teams.action';
import { NotificationListFiltersTeamsChatDto } from '@dto/notification-list/notification-list-filters-teams-chat.dto';
import { NotificationListFiltersTeamsChannelDto } from '@dto/notification-list/notification-list-filters-teams-channel.dto';
import { NotificationListFiltersTeamsCampaignDto } from '@dto/notification-list/notification-list-filters-teams-campaign.dto';

export const initFiltersTeamsChat: NotificationListFiltersTeamsChatDto = {
  productIds: [],
  receiverTypes: [],
  startDate: null,
  endDate: null,
  status: '',
  chatCodes: [],
  sourceId: '',
  receiver: '',
};

export const initFiltersTeamsChannel: NotificationListFiltersTeamsChannelDto = {
  productIds: [],
  startDate: null,
  endDate: null,
  status: '',
  sourceId: '',
  receiver: '',
};

export const initFiltersTeamsCampaign: NotificationListFiltersTeamsCampaignDto = {
  productIds: [],
  startDate: null,
  endDate: null,
  status: '',
  chatCodes: [],
  sourceId: '',
  receiver: '',
  campaignName: '',
};

export interface NotificationListTeamsTabState {
  results: {
    data: NotificationDto[];
    isLoading: boolean;
    error: NullableType<string>;
  };
  pagination: NotificationPagination;
}

export interface NotificationListTeamsState {
  activeTab: 'conversations' | 'channels' | 'campaigns';
  chats: NotificationListTeamsTabState;
  channels: NotificationListTeamsTabState;
  campaigns: NotificationListTeamsTabState;
}

function initialTabState(): NotificationListTeamsTabState {
  return {
    results: { data: [], isLoading: false, error: null },
    pagination: getInitialNotificationPagination(),
  };
}

export const initialStateTeams: NotificationListTeamsState = {
  activeTab: 'conversations',
  chats: initialTabState(),
  channels: initialTabState(),
  campaigns: initialTabState(),
};

export const notificationListTeamsReducer = createReducer(
  initialStateTeams,

  on(setActiveTab, (state, { tab }) => ({
    ...state,
    activeTab: tab,
    chats: tab === 'conversations' ? initialTabState() : state.chats,
    channels: tab === 'channels' ? initialTabState() : state.channels,
    campaigns: tab === 'campaigns' ? initialTabState() : state.campaigns,
  })),

  on(initActiveTab, (state, { tab }) => ({
    ...state,
    activeTab: tab,
  })),

  // ─── Chats ────────────────────────────────────────────────────────────────
  on(loadNotificationListTeamsChats, (state, { currentPageNumber, pageSize }) => ({
    ...state,
    chats: {
      ...state.chats,
      results: { ...state.chats.results, isLoading: true, error: null },
      pagination: { ...state.chats.pagination, currentPageNumber, pageSize },
    },
  })),

  on(loadNotificationListTeamsChatsSuccess, (state, { notificationList }) => ({
    ...state,
    chats: {
      ...state.chats,
      results: { data: notificationList.notifications, isLoading: false, error: null },
      pagination: {
        ...state.chats.pagination,
        totalNotifications: notificationList.totalNotifications,
        totalPages: notificationList.totalPages,
      },
    },
  })),

  on(loadNotificationListTeamsChatsFailure, (state, { error }) => ({
    ...state,
    chats: {
      ...state.chats,
      results: { data: [], isLoading: false, error },
      pagination: getInitialNotificationPagination(),
    },
  })),

  // ─── Channels ─────────────────────────────────────────────────────────────
  on(loadNotificationListTeamsChannels, (state, { currentPageNumber, pageSize }) => ({
    ...state,
    channels: {
      ...state.channels,
      results: { ...state.channels.results, isLoading: true, error: null },
      pagination: { ...state.channels.pagination, currentPageNumber, pageSize },
    },
  })),

  on(loadNotificationListTeamsChannelsSuccess, (state, { notificationList }) => ({
    ...state,
    channels: {
      ...state.channels,
      results: { data: notificationList.notifications, isLoading: false, error: null },
      pagination: {
        ...state.channels.pagination,
        totalNotifications: notificationList.totalNotifications,
        totalPages: notificationList.totalPages,
      },
    },
  })),

  on(loadNotificationListTeamsChannelsFailure, (state, { error }) => ({
    ...state,
    channels: {
      ...state.channels,
      results: { data: [], isLoading: false, error },
      pagination: getInitialNotificationPagination(),
    },
  })),

  // ─── Campaigns ────────────────────────────────────────────────────────────
  on(loadNotificationListTeamsCampaigns, (state, { currentPageNumber, pageSize }) => ({
    ...state,
    campaigns: {
      ...state.campaigns,
      results: { ...state.campaigns.results, isLoading: true, error: null },
      pagination: { ...state.campaigns.pagination, currentPageNumber, pageSize },
    },
  })),

  on(loadNotificationListTeamsCampaignsSuccess, (state, { notificationList }) => ({
    ...state,
    campaigns: {
      ...state.campaigns,
      results: { data: notificationList.notifications, isLoading: false, error: null },
      pagination: {
        ...state.campaigns.pagination,
        totalNotifications: notificationList.totalNotifications,
        totalPages: notificationList.totalPages,
      },
    },
  })),

  on(loadNotificationListTeamsCampaignsFailure, (state, { error }) => ({
    ...state,
    campaigns: {
      ...state.campaigns,
      results: { data: [], isLoading: false, error },
      pagination: getInitialNotificationPagination(),
    },
  }))
);
